/**
 * Roadmap Generator Utility
 * Generates phased investment roadmaps based on TAPS prioritization
 * and loss scenario analysis
 */

import { getRoadmapOverrides, generateReportFraming } from './engagementDriver'
import { deriveMarketSegment, getCostModifier, getMarketSegmentDisplayName } from './marketSegment'
import claimsBenchmarks from '../data/claimsBenchmarks.json'

/**
 * Platform-specific recommendation mappings
 * Maps technology scoping responses to tailored recommendations
 */
const PLATFORM_SPECIFIC_RECOMMENDATIONS = {
  // Identity provider recommendations (ts-2)
  'access-and-identity': {
    'Microsoft Entra ID (Azure AD)': {
      'mfa-implementation': 'Enable Microsoft Authenticator with number matching in Entra ID',
      'privileged-access': 'Deploy Azure AD Privileged Identity Management (PIM)',
      'identity-governance': 'Implement Entra ID Governance with access reviews and entitlement management'
    },
    'Okta': {
      'mfa-implementation': 'Enable Okta Verify with push notifications and FastPass',
      'privileged-access': 'Configure Okta Advanced Server Access (ASA) for privileged accounts',
      'identity-governance': 'Deploy Okta Identity Governance for lifecycle management'
    },
    'Google Identity': {
      'mfa-implementation': 'Enable Google 2-Step Verification with security keys',
      'privileged-access': 'Configure Google Workspace admin roles with least privilege',
      'identity-governance': 'Implement Google Cloud Identity governance policies'
    },
    'On-premises Active Directory only': {
      'mfa-implementation': 'Deploy Azure AD Connect with pass-through authentication and MFA, or on-premises MFA solution',
      'privileged-access': 'Implement tiered administration model for Active Directory',
      'identity-governance': 'Consider hybrid identity with Entra ID for modern governance capabilities'
    },
    'None / Local accounts only': {
      'mfa-implementation': 'CRITICAL: Implement centralized identity management - consider Microsoft Entra ID or Okta',
      'privileged-access': 'Establish centralized identity before privileged access management',
      'identity-governance': 'Build foundational identity infrastructure before governance program'
    }
  },
  // Endpoint protection recommendations (ts-4)
  'technology-environment': {
    'Microsoft Defender for Endpoint / Intune': {
      'endpoint-protection': 'Tune Microsoft Defender for Endpoint attack surface reduction (ASR) rules',
      'zero-trust': 'Integrate Defender for Endpoint with Conditional Access for device compliance'
    },
    'CrowdStrike Falcon': {
      'endpoint-protection': 'Optimize CrowdStrike Falcon prevention policies and enable IOA detection',
      'zero-trust': 'Integrate CrowdStrike Zero Trust Assessment (ZTA) scores with access decisions'
    },
    'SentinelOne': {
      'endpoint-protection': 'Configure SentinelOne Storyline Active Response (STAR) rules',
      'zero-trust': 'Leverage SentinelOne Singularity for extended detection and response'
    },
    'Traditional antivirus only (Norton, McAfee, etc.)': {
      'endpoint-protection': 'PRIORITY: Upgrade to modern EDR solution (CrowdStrike, Defender, or SentinelOne)',
      'zero-trust': 'Modern EDR required as foundation for zero trust architecture'
    },
    'None': {
      'endpoint-protection': 'CRITICAL: Deploy endpoint protection immediately - evaluate Defender, CrowdStrike, or SentinelOne',
      'zero-trust': 'Endpoint protection is prerequisite for zero trust - address immediately'
    }
  },
  // Backup solution recommendations (ts-5)
  'technology-and-systems': {
    'Veeam': {
      'backup-validation': 'Configure Veeam SureBackup for automated recovery testing',
      'third-party-risk': 'Ensure Veeam backup repositories are immutable and air-gapped'
    },
    'Datto/Kaseya': {
      'backup-validation': 'Enable Datto BCDR screenshot verification and automated recovery testing',
      'third-party-risk': 'Verify Datto cloud replication and retention policies'
    },
    'Acronis': {
      'backup-validation': 'Configure Acronis Cyber Protect backup validation schedules',
      'third-party-risk': 'Enable Acronis Active Protection anti-ransomware features'
    },
    'Cloud-native backup (Azure Backup, AWS Backup, Google)': {
      'backup-validation': 'Implement automated restore testing with cloud-native disaster recovery',
      'third-party-risk': 'Review cloud backup retention, replication, and access controls'
    },
    'Manual/ad hoc backups': {
      'backup-validation': 'CRITICAL: Implement automated backup solution with verified recovery',
      'third-party-risk': 'Manual backups are insufficient - deploy enterprise backup immediately'
    },
    'None': {
      'backup-validation': 'CRITICAL: Deploy backup solution immediately - evaluate Veeam, Datto, or cloud-native options',
      'third-party-risk': 'No backup capability represents critical business continuity risk'
    }
  },
  // Network security recommendations (ts-6)
  'operations-and-technology': {
    'Fortinet (FortiGate)': {
      'network-segmentation': 'Configure FortiGate VDOM for network segmentation',
      'config-management': 'Enable FortiManager for centralized configuration and policy management'
    },
    'Palo Alto Networks': {
      'network-segmentation': 'Implement Palo Alto micro-segmentation with App-ID policies',
      'config-management': 'Deploy Panorama for centralized firewall management'
    },
    'Cisco Meraki': {
      'network-segmentation': 'Configure Meraki group policies and VLANs for segmentation',
      'config-management': 'Leverage Meraki dashboard for configuration consistency'
    },
    'ISP-provided router/firewall only': {
      'network-segmentation': 'PRIORITY: Deploy business-class firewall - evaluate Fortinet, Palo Alto, or Meraki',
      'config-management': 'ISP equipment lacks enterprise security capabilities'
    }
  }
}

/**
 * Get platform-specific recommendation text
 */
function getPlatformSpecificRecommendation(categoryId, initiativeId, technologyScoping) {
  if (!technologyScoping) return null

  // Map categories to technology scoping question IDs
  const categoryToTechQuestion = {
    'access-and-identity': 'ts-2',  // Identity provider
    'technology-environment': 'ts-4', // Endpoint protection
    'technology-and-systems': 'ts-5', // Backup solution
    'operations-and-technology': 'ts-6' // Network security
  }

  const techQuestionId = categoryToTechQuestion[categoryId]
  if (!techQuestionId) return null

  const techResponse = technologyScoping[techQuestionId]
  if (!techResponse?.value) return null

  const categoryRecommendations = PLATFORM_SPECIFIC_RECOMMENDATIONS[categoryId]
  if (!categoryRecommendations) return null

  const platformRecommendations = categoryRecommendations[techResponse.value]
  if (!platformRecommendations) return null

  return platformRecommendations[initiativeId] || null
}

/**
 * Initiative templates mapped to maturity categories
 */
const INITIATIVE_CATALOG = {
  'access-and-identity': [
    { id: 'mfa-implementation', name: 'Multi-Factor Authentication', effort: 'Medium', phase: 'quick-wins', costRange: { min: 5000, max: 25000 } },
    { id: 'password-policy', name: 'Password Policy Enhancement', effort: 'Low', phase: 'quick-wins', costRange: { min: 2000, max: 8000 } },
    { id: 'privileged-access', name: 'Privileged Access Management', effort: 'High', phase: 'strategic', costRange: { min: 25000, max: 75000 } },
    { id: 'identity-governance', name: 'Identity Governance Program', effort: 'High', phase: 'long-term', costRange: { min: 50000, max: 150000 } }
  ],
  'ai-adoption': [
    { id: 'ai-inventory', name: 'AI Tool Inventory & Assessment', effort: 'Low', phase: 'quick-wins', costRange: { min: 3000, max: 10000 } },
    { id: 'ai-usage-policy', name: 'AI Usage Policy Development', effort: 'Medium', phase: 'quick-wins', costRange: { min: 5000, max: 15000 } },
    { id: 'ai-security-controls', name: 'AI Security Controls Implementation', effort: 'High', phase: 'strategic', costRange: { min: 20000, max: 60000 } }
  ],
  'ai-governance': [
    { id: 'ai-governance-framework', name: 'AI Governance Framework', effort: 'Medium', phase: 'strategic', costRange: { min: 15000, max: 45000 } },
    { id: 'ai-risk-assessment', name: 'AI Risk Assessment Program', effort: 'Medium', phase: 'strategic', costRange: { min: 10000, max: 30000 } },
    { id: 'ai-monitoring', name: 'AI Output Monitoring', effort: 'High', phase: 'long-term', costRange: { min: 30000, max: 100000 } }
  ],
  'data-privacy': [
    { id: 'data-classification', name: 'Data Classification Program', effort: 'Medium', phase: 'quick-wins', costRange: { min: 8000, max: 25000 } },
    { id: 'privacy-training', name: 'Privacy Awareness Training', effort: 'Low', phase: 'quick-wins', costRange: { min: 3000, max: 10000 } },
    { id: 'dlp-implementation', name: 'Data Loss Prevention (DLP)', effort: 'High', phase: 'strategic', costRange: { min: 35000, max: 100000 } },
    { id: 'privacy-program', name: 'Comprehensive Privacy Program', effort: 'High', phase: 'long-term', costRange: { min: 50000, max: 150000 } }
  ],
  'governance-and-strategy': [
    { id: 'security-charter', name: 'Security Program Charter', effort: 'Low', phase: 'quick-wins', costRange: { min: 2000, max: 8000 } },
    { id: 'policy-review', name: 'Security Policy Review & Update', effort: 'Medium', phase: 'quick-wins', costRange: { min: 5000, max: 15000 } },
    { id: 'governance-framework', name: 'Security Governance Framework', effort: 'High', phase: 'strategic', costRange: { min: 25000, max: 75000 } },
    { id: 'board-reporting', name: 'Board-Level Reporting Program', effort: 'Medium', phase: 'strategic', costRange: { min: 10000, max: 30000 } }
  ],
  'incident-readiness': [
    { id: 'ir-plan', name: 'Incident Response Plan Development', effort: 'Medium', phase: 'quick-wins', costRange: { min: 8000, max: 25000 } },
    { id: 'ir-retainer', name: 'IR Retainer Engagement', effort: 'Low', phase: 'quick-wins', costRange: { min: 15000, max: 40000 } },
    { id: 'tabletop-exercises', name: 'Tabletop Exercise Program', effort: 'Medium', phase: 'strategic', costRange: { min: 10000, max: 30000 } },
    { id: 'siem-soc', name: 'SIEM/SOC Implementation', effort: 'High', phase: 'long-term', costRange: { min: 75000, max: 250000 } }
  ],
  'operations-and-technology': [
    { id: 'patch-management', name: 'Patch Management Program', effort: 'Medium', phase: 'quick-wins', costRange: { min: 10000, max: 35000 } },
    { id: 'vulnerability-scanning', name: 'Vulnerability Scanning', effort: 'Medium', phase: 'quick-wins', costRange: { min: 8000, max: 25000 } },
    { id: 'config-management', name: 'Configuration Management', effort: 'High', phase: 'strategic', costRange: { min: 25000, max: 75000 } },
    { id: 'network-segmentation', name: 'Network Segmentation', effort: 'High', phase: 'long-term', costRange: { min: 50000, max: 200000 } }
  ],
  'people-and-culture': [
    { id: 'security-awareness', name: 'Security Awareness Training', effort: 'Low', phase: 'quick-wins', costRange: { min: 5000, max: 20000 } },
    { id: 'phishing-simulation', name: 'Phishing Simulation Program', effort: 'Low', phase: 'quick-wins', costRange: { min: 3000, max: 12000 } },
    { id: 'security-champions', name: 'Security Champions Program', effort: 'Medium', phase: 'strategic', costRange: { min: 8000, max: 25000 } },
    { id: 'culture-program', name: 'Security Culture Program', effort: 'High', phase: 'long-term', costRange: { min: 20000, max: 60000 } }
  ],
  'risk-and-incident-history': [
    { id: 'risk-register', name: 'Risk Register Implementation', effort: 'Medium', phase: 'quick-wins', costRange: { min: 5000, max: 15000 } },
    { id: 'risk-assessment', name: 'Formal Risk Assessment', effort: 'Medium', phase: 'strategic', costRange: { min: 15000, max: 50000 } },
    { id: 'lessons-learned', name: 'Lessons Learned Process', effort: 'Low', phase: 'quick-wins', costRange: { min: 2000, max: 8000 } },
    { id: 'risk-management', name: 'Enterprise Risk Management', effort: 'High', phase: 'long-term', costRange: { min: 40000, max: 120000 } }
  ],
  'security-and-it-program-structure': [
    { id: 'roles-responsibilities', name: 'Define Security Roles', effort: 'Low', phase: 'quick-wins', costRange: { min: 3000, max: 10000 } },
    { id: 'mssp-evaluation', name: 'MSSP/MDR Evaluation', effort: 'Medium', phase: 'strategic', costRange: { min: 10000, max: 30000 } },
    { id: 'security-staffing', name: 'Security Staffing Plan', effort: 'Medium', phase: 'strategic', costRange: { min: 8000, max: 25000 } },
    { id: 'program-maturation', name: 'Program Maturation Roadmap', effort: 'High', phase: 'long-term', costRange: { min: 25000, max: 75000 } }
  ],
  'technology-environment': [
    { id: 'asset-inventory', name: 'Asset Inventory', effort: 'Medium', phase: 'quick-wins', costRange: { min: 8000, max: 25000 } },
    { id: 'endpoint-protection', name: 'Endpoint Protection Enhancement', effort: 'Medium', phase: 'quick-wins', costRange: { min: 15000, max: 50000 } },
    { id: 'cloud-security', name: 'Cloud Security Posture', effort: 'High', phase: 'strategic', costRange: { min: 30000, max: 100000 } },
    { id: 'zero-trust', name: 'Zero Trust Architecture', effort: 'High', phase: 'long-term', costRange: { min: 100000, max: 350000 } }
  ],
  'technology-and-systems': [
    { id: 'backup-validation', name: 'Backup Validation Testing', effort: 'Low', phase: 'quick-wins', costRange: { min: 3000, max: 10000 } },
    { id: 'vendor-assessment', name: 'Vendor Security Assessment', effort: 'Medium', phase: 'strategic', costRange: { min: 10000, max: 30000 } },
    { id: 'app-security', name: 'Application Security Program', effort: 'High', phase: 'strategic', costRange: { min: 25000, max: 80000 } },
    { id: 'third-party-risk', name: 'Third-Party Risk Management', effort: 'High', phase: 'long-term', costRange: { min: 40000, max: 120000 } }
  ],
  'data-management': [
    { id: 'data-inventory', name: 'Data Inventory & Mapping', effort: 'Medium', phase: 'quick-wins', costRange: { min: 8000, max: 25000 } },
    { id: 'encryption-review', name: 'Encryption Standards Review', effort: 'Medium', phase: 'quick-wins', costRange: { min: 5000, max: 15000 } },
    { id: 'data-governance', name: 'Data Governance Framework', effort: 'High', phase: 'strategic', costRange: { min: 25000, max: 75000 } },
    { id: 'data-lifecycle', name: 'Data Lifecycle Management', effort: 'High', phase: 'long-term', costRange: { min: 35000, max: 100000 } }
  ],
  'program-and-risk-reporting': [
    { id: 'metrics-dashboard', name: 'Security Metrics Dashboard', effort: 'Medium', phase: 'quick-wins', costRange: { min: 8000, max: 25000 } },
    { id: 'executive-reporting', name: 'Executive Reporting Templates', effort: 'Low', phase: 'quick-wins', costRange: { min: 3000, max: 10000 } },
    { id: 'compliance-tracking', name: 'Compliance Tracking System', effort: 'Medium', phase: 'strategic', costRange: { min: 15000, max: 45000 } },
    { id: 'grc-platform', name: 'GRC Platform Implementation', effort: 'High', phase: 'long-term', costRange: { min: 50000, max: 175000 } }
  ]
}

/**
 * Phase definitions
 */
const PHASES = {
  'quick-wins': {
    id: 'quick-wins',
    name: 'Quick Wins',
    timeline: '0-90 days',
    description: 'Immediate actions with rapid value realization',
    focus: 'Low-effort, high-impact improvements'
  },
  'strategic': {
    id: 'strategic',
    name: 'Strategic Initiatives',
    timeline: '90-180 days',
    description: 'Medium-term projects requiring planning and resources',
    focus: 'Building core capabilities and controls'
  },
  'long-term': {
    id: 'long-term',
    name: 'Long-term Transformation',
    timeline: '180-365 days',
    description: 'Major initiatives for sustained improvement',
    focus: 'Program maturation and advanced capabilities'
  }
}

/**
 * Legacy organization size cost multipliers (for backwards compatibility)
 * Prefer using getCostModifier from marketSegment.js for new code
 */
const SIZE_MULTIPLIERS = {
  'micro': 0.6,
  'small': 0.8,
  'mid-market': 1.0,
  'upper-mid-market': 1.4
}

/**
 * Determine organization size from impact data or business scoping responses.
 * Prefers market segment derivation from business scoping if available.
 *
 * @param {Object} impactData - Impact calibration data
 * @param {Object} businessScopingResponses - Business scoping responses (optional)
 * @returns {string} Market segment ID
 */
function getOrganizationSize(impactData, businessScopingResponses = null) {
  // If business scoping responses provided, use market segment derivation
  if (businessScopingResponses) {
    const segment = deriveMarketSegment(businessScopingResponses)
    return segment.id
  }

  // Fallback to legacy behavior using impact data
  const revenue = impactData?.estimated_annual_revenue || 0
  const employees = impactData?.employee_count || 0

  if (revenue > 100000000 || employees > 250) return 'upper-mid-market'
  if (revenue > 25000000 || employees > 51) return 'mid-market'
  if (revenue > 5000000 || employees > 15) return 'small'
  return 'micro'
}

/**
 * Get cost multiplier from market segment or legacy size calculation
 *
 * @param {Object} impactData - Impact calibration data
 * @param {Object} businessScopingResponses - Business scoping responses (optional)
 * @returns {number} Cost multiplier
 */
function getMarketSegmentCostMultiplier(impactData, businessScopingResponses = null) {
  if (businessScopingResponses) {
    return getCostModifier(businessScopingResponses)
  }
  const size = getOrganizationSize(impactData)
  return SIZE_MULTIPLIERS[size] || 1.0
}

/**
 * Resolve industry from gap analysis to claims benchmark sector profile.
 */
function getClaimsSectorForRoadmap(gapAnalysis) {
  const industry = gapAnalysis?.industry
  if (!industry || !claimsBenchmarks.sectorProfiles) return null

  const profile = claimsBenchmarks.sectorProfiles[industry]
  if (profile && industry !== '_doc') {
    return { key: industry, ...profile }
  }

  const aliases = { 'Legal': 'Legal' }
  const aliasKey = aliases[industry]
  if (aliasKey && claimsBenchmarks.sectorProfiles[aliasKey]) {
    return { key: aliasKey, ...claimsBenchmarks.sectorProfiles[aliasKey] }
  }

  return null
}

/**
 * Build an initiative-level weight map from controlEffectiveness data.
 * Each initiative ID gets a weight proportional to the average cost reduction
 * of the control it maps to. Higher weight = more ROI-impactful initiative.
 */
function getControlEffectivenessWeights() {
  const controls = claimsBenchmarks.controlEffectiveness?.controls
  if (!controls || !controls.length) return {}

  const maxReduction = Math.max(...controls.map(c => c.averageCostReduction || 0))
  if (maxReduction <= 0) return {}

  const weights = {}
  controls.forEach(control => {
    if (!control.mapsToInitiative) return
    const normalized = 0.5 + ((control.averageCostReduction / maxReduction) * 1.5)
    control.mapsToInitiative.forEach(initiativeId => {
      weights[initiativeId] = Math.max(weights[initiativeId] || 0.5, Math.round(normalized * 100) / 100)
    })
  })

  return weights
}

/**
 * Generate investment roadmap based on TAPS prioritization
 *
 * @param {Object} tapsResults - Results from calculateTAPS
 * @param {Object} impactData - Business impact data
 * @param {Object} gapAnalysis - Enhanced gap analysis
 * @param {Object} technologyScoping - Technology scoping responses (optional)
 * @param {Object} engagementContext - Engagement driver context (optional)
 * @param {Object} businessScopingResponses - Business scoping responses for market segment (optional)
 * @returns {Object} Phased investment roadmap
 */
export function generateRoadmap(tapsResults, impactData, gapAnalysis, technologyScoping = null, engagementContext = null, businessScopingResponses = null) {
  const orgSize = getOrganizationSize(impactData, businessScopingResponses)
  const sizeMultiplier = getMarketSegmentCostMultiplier(impactData, businessScopingResponses)
  const marketSegmentName = businessScopingResponses ? getMarketSegmentDisplayName(businessScopingResponses) : null

  // Look up claims data for control-effectiveness-weighted risk reduction
  const claimsSector = getClaimsSectorForRoadmap(gapAnalysis)
  const controlWeights = getControlEffectivenessWeights()

  // Get engagement driver overrides if available
  const engagementOverrides = engagementContext
    ? getRoadmapOverrides(engagementContext)
    : { urgencyMultiplier: 1.0, phaseCompression: false, domainBoosts: {}, focusTags: [] }

  const phases = {
    'quick-wins': { ...PHASES['quick-wins'], initiatives: [], totalCost: { min: 0, max: 0 }, riskReduction: 0 },
    'strategic': { ...PHASES['strategic'], initiatives: [], totalCost: { min: 0, max: 0 }, riskReduction: 0 },
    'long-term': { ...PHASES['long-term'], initiatives: [], totalCost: { min: 0, max: 0 }, riskReduction: 0 }
  }

  // Process top priority categories from TAPS
  const topCategories = tapsResults?.categories?.slice(0, 8) || []

  topCategories.forEach((category, index) => {
    const initiatives = INITIATIVE_CATALOG[category.categoryId] || []
    const priorityMultiplier = 1 - (index * 0.1) // Higher priority = more initiatives

    // Apply engagement driver domain boost
    const domainBoost = engagementOverrides.domainBoosts[category.categoryId] || 1.0

    initiatives.forEach(initiative => {
      // Determine phase - compress if urgent timeline
      let targetPhase = initiative.phase
      if (engagementOverrides.phaseCompression && initiative.phase !== 'quick-wins') {
        // Move strategic initiatives to quick-wins if urgent
        if (initiative.phase === 'strategic' && engagementOverrides.urgencyMultiplier >= 1.5) {
          targetPhase = 'quick-wins'
        }
        // Move long-term to strategic if urgent
        if (initiative.phase === 'long-term' && engagementOverrides.urgencyMultiplier >= 1.5) {
          targetPhase = 'strategic'
        }
      }

      // Add initiative to appropriate phase
      const phaseData = phases[targetPhase]
      if (!phaseData) return

      // Check if initiative already added
      if (phaseData.initiatives.some(i => i.id === initiative.id)) return

      // Scale costs by organization size
      const scaledCost = {
        min: Math.round(initiative.costRange.min * sizeMultiplier),
        max: Math.round(initiative.costRange.max * sizeMultiplier)
      }

      // Get platform-specific recommendation if available
      const platformSpecificText = getPlatformSpecificRecommendation(
        category.categoryId,
        initiative.id,
        technologyScoping
      )

      // Calculate adjusted priority based on domain boost
      const adjustedIndex = domainBoost > 1.0 ? Math.max(0, index - Math.floor((domainBoost - 1) * 5)) : index

      phaseData.initiatives.push({
        ...initiative,
        categoryId: category.categoryId,
        domain: category.domain,
        tapsScore: category.tapsScore,
        priorityRank: adjustedIndex + 1,
        scaledCost,
        priority: adjustedIndex < 3 ? 'High' : adjustedIndex < 6 ? 'Medium' : 'Low',
        // Override name with platform-specific text if available
        name: platformSpecificText || initiative.name,
        originalName: initiative.name,
        isPlatformSpecific: !!platformSpecificText,
        domainBoost: domainBoost > 1.0 ? domainBoost : null,
        phaseCompressed: targetPhase !== initiative.phase
      })

      // Accumulate phase costs
      phaseData.totalCost.min += scaledCost.min
      phaseData.totalCost.max += scaledCost.max
    })
  })

  // Sort initiatives within each phase by priority
  Object.values(phases).forEach(phase => {
    phase.initiatives.sort((a, b) => a.priorityRank - b.priorityRank)

    // Limit initiatives per phase to keep roadmap actionable
    const maxPerPhase = phase.id === 'quick-wins' ? 8 : phase.id === 'strategic' ? 6 : 5
    phase.initiatives = phase.initiatives.slice(0, maxPerPhase)

    // Recalculate costs after limiting
    phase.totalCost = phase.initiatives.reduce(
      (sum, init) => ({
        min: sum.min + init.scaledCost.min,
        max: sum.max + init.scaledCost.max
      }),
      { min: 0, max: 0 }
    )
  })

  // Calculate risk reduction estimates
  phases['quick-wins'].riskReduction = calculatePhaseRiskReduction('quick-wins', phases['quick-wins'].initiatives, tapsResults, controlWeights)
  phases['strategic'].riskReduction = calculatePhaseRiskReduction('strategic', phases['strategic'].initiatives, tapsResults, controlWeights)
  phases['long-term'].riskReduction = calculatePhaseRiskReduction('long-term', phases['long-term'].initiatives, tapsResults, controlWeights)

  // Calculate total roadmap metrics
  const totalInvestment = {
    min: Object.values(phases).reduce((sum, p) => sum + p.totalCost.min, 0),
    max: Object.values(phases).reduce((sum, p) => sum + p.totalCost.max, 0)
  }

  const totalRiskReduction = Math.min(
    100,
    phases['quick-wins'].riskReduction +
    phases['strategic'].riskReduction +
    phases['long-term'].riskReduction
  )

  // Generate report framing if engagement context provided
  const reportFraming = engagementContext
    ? generateReportFraming(engagementContext)
    : null

  return {
    phases: Object.values(phases),
    summary: {
      organizationSize: orgSize,
      marketSegment: marketSegmentName,
      costMultiplier: sizeMultiplier,
      totalInvestment,
      totalInvestmentFormatted: formatCostRange(totalInvestment),
      totalRiskReduction,
      initiativeCount: Object.values(phases).reduce((sum, p) => sum + p.initiatives.length, 0),
      roi: calculateROI(phases, impactData, gapAnalysis, claimsSector)
    },
    claimsBenchmarkContext: claimsSector ? {
      sector: claimsSector.key,
      avgIncidentCost: claimsSector.incidentCost?.fiveYearAverage || null,
      topCausesOfLoss: claimsSector.topCausesOfLoss || [],
      controlEffectivenessApplied: Object.keys(controlWeights).length > 0,
      source: claimsSector.source
    } : null,
    engagementContext: engagementContext ? {
      urgencyMultiplier: engagementOverrides.urgencyMultiplier,
      phaseCompression: engagementOverrides.phaseCompression,
      priorityPhase: engagementOverrides.priorityPhase,
      priorityPhaseLabel: engagementOverrides.priorityPhaseLabel,
      domainBoostsApplied: Object.keys(engagementOverrides.domainBoosts).length > 0
    } : null,
    reportFraming,
    generatedAt: new Date().toISOString()
  }
}

/**
 * Calculate estimated risk reduction for a phase.
 * When controlEffectiveness data is available, weights reduction by validated cost savings.
 */
function calculatePhaseRiskReduction(phaseId, initiatives, tapsResults, controlWeights = {}) {
  if (!initiatives.length) return 0

  // Base reduction by phase
  const baseReduction = {
    'quick-wins': 15,
    'strategic': 25,
    'long-term': 20
  }

  // Adjust based on number, priority, and control effectiveness of initiatives
  const priorityBonus = initiatives.reduce((sum, init) => {
    const priorityPoints = init.priority === 'High' ? 3 : init.priority === 'Medium' ? 2 : 1

    // If control effectiveness data available, boost initiatives with validated cost reduction
    const effectivenessWeight = controlWeights[init.id] || controlWeights[init.originalName] || 1.0
    return sum + Math.round(priorityPoints * effectivenessWeight)
  }, 0)

  return Math.min(40, baseReduction[phaseId] + Math.floor(priorityBonus / 2))
}

/**
 * Calculate return on investment metrics.
 * When claims data is available, uses claims-validated ALE as the exposure anchor.
 */
function calculateROI(phases, impactData, gapAnalysis, claimsSector = null) {
  const totalCost = Object.values(phases).reduce(
    (sum, p) => sum + ((p.totalCost.min + p.totalCost.max) / 2),
    0
  )

  // Estimate risk reduction value based on gap analysis
  const gapALE = gapAnalysis?.summary?.estimatedTotalCost?.max || 0

  // When claims data available, blend gap ALE with sector average incident cost
  let totalALE = gapALE
  let aleSource = 'gap-analysis'

  const claimsAvgCost = claimsSector?.incidentCost?.fiveYearAverage
  if (claimsAvgCost && claimsAvgCost > 0) {
    if (gapALE > 0) {
      totalALE = Math.round((gapALE * 0.5) + (claimsAvgCost * 0.5))
      aleSource = 'blended'
    } else {
      totalALE = claimsAvgCost
      aleSource = 'claims-only'
    }
  }

  const riskReduction = Object.values(phases).reduce((sum, p) => sum + p.riskReduction, 0)
  const annualSavings = (totalALE * riskReduction) / 100

  // Calculate payback period
  const paybackMonths = annualSavings > 0 ? Math.round((totalCost / annualSavings) * 12) : null

  // Calculate 3-year ROI
  const threeYearSavings = annualSavings * 3
  const roi = totalCost > 0 ? Math.round(((threeYearSavings - totalCost) / totalCost) * 100) : 0

  return {
    totalInvestment: totalCost,
    estimatedAnnualSavings: Math.round(annualSavings),
    paybackMonths,
    threeYearROI: roi,
    riskReductionPercent: riskReduction,
    methodology: aleSource === 'blended'
      ? 'Based on TAPS prioritization, loss scenario analysis, and claims benchmark validation'
      : aleSource === 'claims-only'
        ? 'Based on TAPS prioritization and industry claims benchmarks'
        : 'Based on TAPS prioritization and loss scenario analysis',
    aleSource,
    claimsAnchor: claimsSector ? {
      sectorAvgIncidentCost: claimsAvgCost,
      sector: claimsSector.key,
      source: claimsSector.source
    } : null
  }
}

/**
 * Calculate metrics for a specific phase
 */
export function calculatePhaseMetrics(phase, impactData, businessScopingResponses = null) {
  const orgSize = getOrganizationSize(impactData, businessScopingResponses)
  const sizeMultiplier = getMarketSegmentCostMultiplier(impactData, businessScopingResponses)

  return {
    initiativeCount: phase.initiatives.length,
    totalCost: phase.totalCost,
    averageCost: phase.initiatives.length > 0
      ? {
        min: Math.round(phase.totalCost.min / phase.initiatives.length),
        max: Math.round(phase.totalCost.max / phase.initiatives.length)
      }
      : { min: 0, max: 0 },
    highPriorityCount: phase.initiatives.filter(i => i.priority === 'High').length,
    effortBreakdown: {
      low: phase.initiatives.filter(i => i.effort === 'Low').length,
      medium: phase.initiatives.filter(i => i.effort === 'Medium').length,
      high: phase.initiatives.filter(i => i.effort === 'High').length
    },
    riskReduction: phase.riskReduction,
    sizeMultiplier
  }
}

/**
 * Estimate implementation cost for a set of recommendations
 */
export function estimateImplementationCost(recommendations, orgSize = 'medium') {
  const sizeMultiplier = SIZE_MULTIPLIERS[orgSize] || 1.0

  let totalMin = 0
  let totalMax = 0

  recommendations.forEach(rec => {
    // Find matching initiative in catalog
    Object.values(INITIATIVE_CATALOG).forEach(categoryInitiatives => {
      categoryInitiatives.forEach(init => {
        if (init.name.toLowerCase().includes(rec.toLowerCase()) ||
            rec.toLowerCase().includes(init.name.toLowerCase())) {
          totalMin += init.costRange.min * sizeMultiplier
          totalMax += init.costRange.max * sizeMultiplier
        }
      })
    })
  })

  return {
    min: Math.round(totalMin),
    max: Math.round(totalMax),
    formatted: formatCostRange({ min: Math.round(totalMin), max: Math.round(totalMax) })
  }
}

/**
 * Format cost range for display
 */
function formatCostRange(costRange) {
  const formatAmount = (amount) => {
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}M`
    }
    if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(0)}K`
    }
    return `$${amount}`
  }

  return `${formatAmount(costRange.min)} - ${formatAmount(costRange.max)}`
}

/**
 * Get all available initiatives for reference
 */
export function getAllInitiatives() {
  const all = []
  Object.entries(INITIATIVE_CATALOG).forEach(([categoryId, initiatives]) => {
    initiatives.forEach(init => {
      all.push({ ...init, categoryId })
    })
  })
  return all
}

/**
 * Get phase definitions
 */
export function getPhaseDefinitions() {
  return Object.values(PHASES)
}

/**
 * Generate executive summary of roadmap
 */
export function generateExecutiveSummary(roadmap, tapsResults, lossScenarios) {
  const topPriorities = tapsResults?.categories?.slice(0, 3) || []
  const highestRisk = lossScenarios?.scenarios?.[0]

  return {
    headline: `${roadmap.summary.initiativeCount} initiatives across 3 phases to reduce risk by ${roadmap.summary.totalRiskReduction}%`,
    investment: roadmap.summary.totalInvestmentFormatted,
    timeline: '12 months',
    topPriorities: topPriorities.map(p => ({
      domain: p.domain,
      tapsScore: p.tapsScore,
      priorityLevel: p.priorityLevel
    })),
    primaryRisk: highestRisk ? {
      scenario: highestRisk.scenarioName,
      annualizedExposure: highestRisk.aleFormatted,
      probability: highestRisk.probabilityPercent
    } : null,
    roi: roadmap.summary.roi,
    quickWinsCount: roadmap.phases.find(p => p.id === 'quick-wins')?.initiatives.length || 0,
    immediateActions: roadmap.phases
      .find(p => p.id === 'quick-wins')
      ?.initiatives
      .slice(0, 3)
      .map(i => i.name) || [],
    claimsContext: roadmap.claimsBenchmarkContext ? {
      sectorAvgIncidentCost: roadmap.claimsBenchmarkContext.avgIncidentCost,
      topCause: roadmap.claimsBenchmarkContext.topCausesOfLoss?.[0] || null,
      source: roadmap.claimsBenchmarkContext.source,
      dataWindow: '2020-2024'
    } : null
  }
}
